import { useEffect, useRef, useState } from 'react';
import { SITE_FRAME_SANDBOX } from '../../../../lib/html-doc-frame';
import { RECORDER_EXCLUDE_CLASS } from '../../../../lib/recorder-mask';
import { Typography } from '../../ui';
import styles from './InlineHtmlPreview.module.css';

export const INLINE_PREVIEW_HEIGHT = 255;

let measuredGutter: number | undefined;

export function scrollbarGutter(): number {
  if (measuredGutter !== undefined) return measuredGutter;
  if (typeof document === 'undefined' || !document.body) return 0;
  const probe = document.createElement('div');
  probe.style.cssText = 'position:absolute;top:-9999px;width:100px;height:100px;overflow:scroll';
  document.body.appendChild(probe);
  measuredGutter = Math.max(0, probe.offsetWidth - probe.clientWidth);
  probe.remove();
  return measuredGutter;
}

function scrollRoot(element: Element): Element | null {
  for (let node = element.parentElement; node; node = node.parentElement) {
    if (/auto|scroll|overlay/.test(getComputedStyle(node).overflowY)) return node;
  }
  return null;
}

export function inlineFrameLayout(naturalWidth: number, width: number, gutter: number): { frameWidth: number; scale: number } {
  const frameWidth = Math.max(naturalWidth + gutter, width);
  return { frameWidth, scale: width > 0 ? Math.min(1, width / frameWidth) : 1 };
}

export function InlineHtmlPreview({
  html,
  src,
  title,
  naturalWidth,
  status,
  failed = false,
  onSeen,
  testId,
}: {
  html?: string;
  src?: string;
  title: string;
  naturalWidth: number;
  status: string;
  failed?: boolean;
  onSeen?: () => void;
  testId?: string;
}) {
  const container = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const seen = useRef(onSeen);
  seen.current = onSeen;
  useEffect(() => {
    const element = container.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const element = container.current;
    if (!element) return;
    if (typeof IntersectionObserver === 'undefined') {
      seen.current?.();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) seen.current?.();
      },
      { root: scrollRoot(element), rootMargin: '200px 0px' },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const { frameWidth, scale } = inlineFrameLayout(naturalWidth, width, 0);
  const frameStyle = { width: frameWidth, height: INLINE_PREVIEW_HEIGHT / scale, transform: `scale(${scale})` };
  const viewportStyle = { width: frameWidth * scale, height: INLINE_PREVIEW_HEIGHT };
  return (
    <div ref={container} className={styles.preview} data-testid={testId} aria-busy={!html && !src && !failed}>
      {src ? (
        <div className={styles.viewport} style={viewportStyle}>
          <iframe
            title={title}
            src={src}
            sandbox={SITE_FRAME_SANDBOX}
            referrerPolicy="no-referrer"
            scrolling="no"
            className={`${styles.frame} ${RECORDER_EXCLUDE_CLASS}`}
            style={frameStyle}
          />
        </div>
      ) : html ? (
        <div className={styles.viewport} style={viewportStyle}>
          <iframe title={title} srcDoc={html} sandbox="" referrerPolicy="no-referrer" scrolling="no" className={styles.frame} style={frameStyle} />
        </div>
      ) : (
        <div className={styles.status} role={failed ? 'alert' : 'status'}>
          <Typography variant="p3" color="secondary">
            {status}
          </Typography>
        </div>
      )}
    </div>
  );
}
